// Welcome Bonus — how long the header keeps saying "BONUS EXPIRED" (KIOSK-459).
//
// The status API reports an expired bonus for ever, so the chip used to stay in the header for
// good and read as a permanent bad state. An expiry is news, and the Player needs it for a while:
// it is why their balance dropped (the grant was recalled). So the chip is shown until either
//   - the Player has opened it and read what happened, or
//   - a week has passed since the bonus expired,
// whichever comes first. After that there is no bonus chip at all.
//
// When it expired: `expiredAt` from the status API when it sends one. Older APIs do not, so the
// fallback is the first time this device saw the bonus expired. Both that time and "has read it"
// are kept per account: kiosk devices are shared.

import { apiService } from '@/services/apiService';
import { accountIdFromToken } from '@/utils/accountId';
import type { SignupBonus } from './signupBonusService';

/** How long after it expired the chip is still shown to a Player who never opened it. */
export const EXPIRED_NOTICE_MS = 7 * 24 * 60 * 60 * 1000;

const KEY_PREFIX = 'sb-expired-notice:';
const UNKNOWN_ACCOUNT = 'unknown';

interface NoticeRecord {
  /** When this device first saw the bonus expired (ms since epoch). */
  firstSeenAt?: number;
  /** The Player opened the chip and closed the explanation. */
  read?: boolean;
}

function storageKey(): string {
  return KEY_PREFIX + (accountIdFromToken(apiService.getAccessToken()) ?? UNKNOWN_ACCOUNT);
}

function readRecord(): NoticeRecord {
  try {
    const raw = localStorage.getItem(storageKey());
    const parsed = raw ? (JSON.parse(raw) as NoticeRecord | null) : null;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeRecord(record: NoticeRecord): void {
  try {
    localStorage.setItem(storageKey(), JSON.stringify(record));
  } catch {
    /* storage unavailable (private mode): the chip follows the API's expiredAt, or stays */
  }
}

function expiredAtMs(bonus: SignupBonus, record: NoticeRecord): number | null {
  const fromApi = bonus.expiredAt ? Date.parse(bonus.expiredAt) : NaN;
  if (Number.isFinite(fromApi)) return fromApi;
  return typeof record.firstSeenAt === 'number' ? record.firstSeenAt : null;
}

/**
 * True while an expired bonus still belongs in the header. Any other status is not this module's
 * business and returns true.
 */
export function isExpiredBonusNoticeDue(bonus: SignupBonus | null | undefined, now: number = Date.now()): boolean {
  if (!bonus || bonus.status !== 'expired') return true;
  const record = readRecord();
  if (record.read) return false;
  const since = expiredAtMs(bonus, record);
  // Never seen before and the API gave no time: this is the first sight of it.
  if (since === null) return true;
  return now - since < EXPIRED_NOTICE_MS;
}

/** Remember when this device first saw the bonus expired (the clock for APIs without `expiredAt`). */
export function noteExpiredBonusSeen(now: number = Date.now()): void {
  const record = readRecord();
  if (typeof record.firstSeenAt === 'number') return;
  writeRecord({ ...record, firstSeenAt: now });
}

/** The Player opened the chip and closed the explanation: they know. No chip from here on. */
export function markExpiredBonusRead(): void {
  writeRecord({ ...readRecord(), read: true });
}
